import { JBaseApi } from '../base';
import {
    ApiWrapper,
    CreateFaqCategory,
    CreateFaqTheme,
    EditFaqCategory,
    ExtendedFaqCategory,
    FaqAccess,
    FaqList,
    FaqView,
    FaqHookProperties,
    FaqItemList,
    FaqTaxonomy,
    jArticleLittle,
    Orders,
    PagingResults,
    DeleteFaqTheme,
    EditFaqTheme,
    ApiDefaultWrapper,
    FormatList,
    CreateFaqEntry,
    FaqItemView,
    AddFaqEntry,
    EditFaqEntry,
    MoveFaqEntry,
    MoveFaqTheme,
} from '../../types';
import { OF } from '../../util';

export class JFaqApi extends JBaseApi {
    public getCategoriesList<T extends ExtendedFaqCategory<FaqList> = ExtendedFaqCategory<FaqList>>(
        format?: FormatList,
        orders?: Orders,
    ): Promise<ApiWrapper<PagingResults<T>>> {
        return this.network.post({
            o: 'objectList',
            f: 'faqQueryList',
            format: format ?? 'raw-list',
            orders: orders ?? [{ name: 'dateCreation', sort: 'ASC' }],
        } as OF);
    }

    public createCategory<T extends jArticleLittle = FaqView>(
        params: Omit<CreateFaqCategory, 'type'>,
        format?: FormatList,
    ): Promise<ApiWrapper<Array<T>>> {
        return this.jApi.article.create<T, CreateFaqCategory>({ ...params, type: 'faq' }, format ?? 'raw-view');
    }

    public updateCategory<T extends jArticleLittle = FaqView>(
        params: Omit<EditFaqCategory, 'type'>,
        format?: FormatList,
    ): Promise<ApiWrapper<T>> {
        return this.jApi.article.update<EditFaqCategory & { format: FormatList }, T>({
            ...params,
            type: 'faq',
            format: format ?? 'raw-view',
        });
    }

    public getCategory<T extends jArticleLittle = FaqView>(id: number, format?: FormatList): Promise<ApiWrapper<T>> {
        return this.jApi.article.get<T>({ idArticle: id, format: format ?? 'raw-view' });
    }

    public deleteCategory(id: number): Promise<ApiWrapper<boolean>> {
        return this.jApi.article.delete(id);
    }

    public checkItem<T extends FaqItemList>(
        query: string,
        page?: number,
        format?: FormatList,
    ): Promise<ApiWrapper<PagingResults<T & { _percent: number }>>> {
        return this.network.post({
            o: 'faq',
            f: 'checkItem',
            query,
            page: page ?? 1,
            format: format ?? 'raw-list',
        } as OF);
    }

    public getTaxonomy(): Promise<ApiWrapper<FaqTaxonomy>> {
        return this.network.post({
            o: 'faq',
            f: 'getFaqTaxonomy',
        });
    }

    public getHookProperties(properties?: Array<keyof FaqHookProperties>): Promise<ApiWrapper<FaqHookProperties>> {
        return this.network.post({
            o: 'faq',
            f: 'getHookProperties',
            properties: properties ?? ['_web'],
        } as OF);
    }

    public getAccess(): Promise<ApiWrapper<FaqAccess>> {
        return this.network.post({
            o: 'faq',
            f: 'access',
        });
    }

    public createTheme<T extends jArticleLittle = FaqView>(
        params: CreateFaqTheme,
        format?: FormatList,
    ): Promise<ApiWrapper<T>> {
        return this.network.postCSRF({
            o: 'faq',
            f: 'add-theme',
            ...params,
            format: format ?? 'raw-view',
        } as OF);
    }

    public deleteTheme<T extends jArticleLittle = FaqView>(
        params: DeleteFaqTheme,
        format?: FormatList,
    ): Promise<ApiWrapper<T>> {
        return this.network.postCSRF({
            o: 'faq',
            f: 'delete-theme',
            ...params,
            format: format ?? 'raw-view',
        } as OF);
    }

    public updateTheme(params: EditFaqTheme, format?: FormatList): Promise<ApiDefaultWrapper> {
        return this.network.postCSRF({
            o: 'faq',
            f: 'set-theme',
            ...params,
            format: format ?? 'raw-view',
        } as OF);
    }

    public getEntry<T extends jArticleLittle = FaqItemView>(uri: string, format?: FormatList): Promise<ApiWrapper<T>> {
        return this.network.post({
            o: 'faq',
            f: 'get-entry',
            uri,
            format: format ?? 'raw-view',
        } as OF);
    }

    public deleteEntry(id: number): Promise<ApiWrapper<boolean>> {
        return this.jApi.article.delete(id);
    }

    public createEntry<T extends jArticleLittle = FaqItemView>(
        params: Omit<CreateFaqEntry, 'type'>,
        format?: FormatList,
    ): Promise<ApiWrapper<Array<T>>> {
        return this.jApi.article.create<T, CreateFaqEntry>(
            {
                ...params,
                idSpot: ['content'],
                type: 'faqItem',
            },
            format ?? 'raw-view',
        );
    }

    public addEntry<T extends jArticleLittle = FaqView>(
        params: AddFaqEntry,
        format?: FormatList,
    ): Promise<ApiWrapper<T>> {
        return this.network.postCSRF({
            o: 'faq',
            f: 'add-entry',
            ...params,
            format: format ?? 'raw-view',
        } as OF);
    }

    public updateEntry<T extends jArticleLittle = FaqItemView>(
        params: Omit<EditFaqEntry, 'type'>,
        format?: FormatList,
    ): Promise<ApiWrapper<T>> {
        return this.jApi.article.update<EditFaqEntry & { format: FormatList }, T>({
            ...params,
            type: 'faqItem',
            format: format ?? 'raw-view',
        });
    }

    public moveEntry<T extends jArticleLittle = FaqView>(
        params: MoveFaqEntry,
        format?: FormatList,
    ): Promise<
        ApiWrapper<{
            source?: T;
            target: T;
        }>
    > {
        return this.network.postCSRF({
            o: 'faq',
            f: 'move-entry',
            ...params,
            format: format ?? 'raw-view',
        } as OF);
    }

    public moveTheme<T extends jArticleLittle = FaqView>(
        params: MoveFaqTheme,
        format?: FormatList,
    ): Promise<
        ApiWrapper<{
            source: T;
            target: T;
        }>
    > {
        return this.network.postCSRF({
            o: 'faq',
            f: 'move-theme',
            ...params,
            format: format ?? 'raw-view',
        } as OF);
    }
}
